import React, { Children, Fragment, HTMLAttributes, ReactNode, isValidElement, useContext } from 'react';
import classNames from 'classnames';
import { BaseText } from '../text';
import { semantic_colors } from '../../foundation';
import { Divider } from '../divider';
import {
  menu_section_css,
  menu_section_divider_css,
  menu_section_label_css,
  menu_section_list_css,
  menu_section_sub_label_css,
  menu_group_section_css,
  menu_group_section_label_css,
  menu_group_section_list_css,
} from './styles';
import { MenuGroupIdContext } from './context';

export interface MenuSectionProps extends Omit<HTMLAttributes<HTMLLIElement>, 'children'> {
  label: string;
  /**
   * 섹션 라벨 우측에 구분선을 표시할지 여부
   * @default false
   */
  divider?: boolean;
  /** 섹션에 포함할 MenuItem 또는 MenuGroup */
  children: ReactNode;
}

const hasSubMenuItem = (children: ReactNode): boolean =>
  Children.toArray(children).some((child) => {
    if (!isValidElement<{ variant?: string; children?: ReactNode }>(child)) {
      return false;
    }
    if (child.type === Fragment) {
      return hasSubMenuItem(child.props.children);
    }
    return child.props.variant === 'sub';
  });

export const MenuSection = ({ className, label, divider = false, children, ...props }: MenuSectionProps) => {
  const groupId = useContext(MenuGroupIdContext);
  const isGroupSection = Boolean(groupId);
  const isSubMenuSection = hasSubMenuItem(children);

  return (
    <li
      role='presentation'
      className={classNames('pds-menu-section', className)}
      css={[menu_section_css, isGroupSection && menu_group_section_css]}
      {...props}
    >
      <div
        css={[
          menu_section_label_css,
          isSubMenuSection && menu_section_sub_label_css,
          isGroupSection && menu_group_section_label_css,
        ]}
      >
        <BaseText typography='Body_12_SemiBold' color={semantic_colors.content.tertiary}>
          {label}
        </BaseText>
        {divider && <Divider direction='horizontal' size='small' css={menu_section_divider_css} />}
      </div>
      <ul role='menu' css={[menu_section_list_css, isGroupSection && menu_group_section_list_css]}>
        {children}
      </ul>
    </li>
  );
};
